/**
 * Funnel tracking for the public marketing site.
 *
 * WHY THIS FILE: this logic used to live inside InstantReport.tsx, so the ONLY
 * page on the whole marketing site that reported anything was the lead magnet.
 * Every other page — pricing, the homepage, the /funnel landers — was invisible,
 * which means the funnel report could show leads and signups but never the
 * traffic that produced them. Conversion rate with no denominator is a vibe.
 *
 * CONSENT SPLIT (unchanged, just moved): the bare event is cookieless,
 * first-party, and carries no visitor identifier — the Plausible model — so it
 * always fires. The ENRICHING signals (document.referrer + UTM tags), which can
 * reveal where a specific visitor came from, are attached only after the
 * visitor affirmatively accepted analytics cookies. hasAnalyticsConsent() also
 * honours a GPC opt-out signal.
 *
 * The server derives props.channel + props.referrer_host from what we send and
 * ignores any channel we might claim (apps/api/src/routes/track.ts).
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { hasAnalyticsConsent } from '../components/layout/CookieBanner';

// Deliberately a local const rather than lib/api.ts's getApiBase(): that module
// imports the Supabase client, and pulling it in here would drag the supabase
// chunk onto every marketing page just to POST an analytics beacon.
const API_URL =
  import.meta.env.VITE_API_URL ??
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:8787'
    : 'https://api.waypoint-intel.com');

/** Events the public sink accepts. Mirrors ALLOWED_EVENTS in the API route. */
export type TrackEvent = 'page_view' | 'instant_report_view' | 'instant_report_submit';

export interface TrackPayload {
  source?: string | null;
  domain?: string | null;
  props?: Record<string, unknown>;
}

/**
 * Parse UTM params from the current URL. Only the five standard keys, and only
 * when present — so the server gets a clean, small object.
 */
export function readUtmFromUrl(): Record<string, string> {
  try {
    const p = new URLSearchParams(window.location.search);
    const out: Record<string, string> = {};
    for (const [param, key] of [
      ['utm_source', 'source'],
      ['utm_medium', 'medium'],
      ['utm_campaign', 'campaign'],
      ['utm_term', 'term'],
      ['utm_content', 'content'],
    ] as const) {
      const v = p.get(param);
      if (v && v.trim()) out[key] = v.trim();
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * Fire-and-forget funnel analytics. Never blocks the UX and swallows every
 * error — the site must work identically when analytics is down.
 */
export function trackEvent(event: TrackEvent, payload: TrackPayload = {}): void {
  try {
    const enrich = hasAnalyticsConsent();
    const referrer =
      enrich && typeof document !== 'undefined' && document.referrer ? document.referrer : null;
    void fetch(`${API_URL}/v1/public/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event,
        source: payload.source ?? null,
        domain: payload.domain ?? null,
        referrer,
        utm: enrich ? readUtmFromUrl() : {},
        props: payload.props ?? {},
      }),
      keepalive: true,
    }).catch(() => {
      /* analytics is best-effort — never surface to the user */
    });
  } catch {
    /* swallow */
  }
}

/**
 * Fire one `page_view` per pathname the visitor actually lands on.
 *
 * Keyed on pathname, not on mount: React StrictMode runs effects twice in dev,
 * and the layout this hook lives in does NOT remount between marketing routes,
 * so a plain mount-once ref would report the landing page and nothing after it.
 * Comparing against the last REPORTED pathname gets both cases right.
 *
 * The search string is deliberately not sent — the server strips it anyway, and
 * query strings on our own site routinely carry UTM tags and prospect emails.
 */
export function usePageView(): void {
  const lastPath = useRef<string | null>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    if (lastPath.current === pathname) return;
    lastPath.current = pathname;
    trackEvent('page_view', { props: { path: pathname } });
  }, [pathname]);
}
